import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API } from "@/App";
import { Bell, Send, Clock, Info } from "lucide-react";
import { toast } from "sonner";

const AdminNotifications = () => {
  const { token } = useAuth();
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [targetGroup, setTargetGroup] = useState("all");
  const [scheduledTime, setScheduledTime] = useState("");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!title || !message) {
      toast.error("Title and message are required");
      return;
    }

    setSending(true);
    
    // Note: This is a placeholder - OneSignal integration would go here
    toast.info("Push notification system requires OneSignal setup");
    toast.success(`Notification "${title}" ready to send to ${targetGroup} users`);
    
    // Reset form
    setTitle("");
    setMessage("");
    setTargetGroup("all");
    setScheduledTime("");
    setSending(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Bell className="w-6 h-6 text-primary" />
        <h2 className="font-heading text-xl font-bold text-secondary">
          Push Notifications
        </h2>
      </div>

      {/* Info Card */}
      <Card className="p-4 bg-primary/5 border border-primary/20">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-secondary">OneSignal Integration Required</p>
            <p className="text-sm text-muted-foreground mt-1">
              Push notifications require OneSignal setup. Once configured, you'll be able to send notifications to users who have opted in.
            </p>
          </div>
        </div>
      </Card>

      {/* Create Notification Form */}
      <Card className="p-6 border border-orange-100">
        <h3 className="font-medium text-secondary mb-4">Create Notification</h3>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-secondary">Title</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Time to log your glucose!"
              className="rounded-xl mt-1"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-secondary">Message</label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g., Don't forget to check your blood sugar today."
              className="rounded-xl mt-1 min-h-[100px]"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-secondary">Target Group</label>
            <Select value={targetGroup} onValueChange={setTargetGroup}>
              <SelectTrigger className="rounded-xl mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                <SelectItem value="trial">Trial Users</SelectItem>
                <SelectItem value="premium">Premium Users</SelectItem>
                <SelectItem value="partner">Partner Users</SelectItem>
                <SelectItem value="inactive">Inactive Users (7+ days)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium text-secondary">
              Schedule (Optional)
            </label>
            <Input
              type="datetime-local"
              value={scheduledTime}
              onChange={(e) => setScheduledTime(e.target.value)}
              className="rounded-xl mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Leave empty to send immediately
            </p>
          </div>
          
          <Button
            onClick={handleSend}
            disabled={sending || !title || !message}
            className="w-full rounded-full bg-primary text-white py-5"
          >
            {scheduledTime ? (
              <>
                <Clock className="w-4 h-4 mr-2" />
                Schedule Notification
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Notification
              </>
            )}
          </Button>
        </div>
      </Card>

      {/* Notification Templates */}
      <Card className="p-6 border border-orange-100">
        <h3 className="font-medium text-secondary mb-4">Quick Templates</h3>
        
        <div className="space-y-3">
          <button
            onClick={() => {
              setTitle("Good morning!");
              setMessage("Don't forget to log your fasting glucose today.");
            }}
            className="w-full p-3 text-left bg-orange-50/50 rounded-xl hover:bg-orange-50 transition-colors"
          >
            <p className="font-medium text-secondary">Morning Check-In</p>
            <p className="text-sm text-muted-foreground">Remind users to log fasting glucose</p>
          </button>
          
          <button
            onClick={() => {
              setTitle("Have you analyzed your meal?");
              setMessage("Snap a photo of your meal to get carb insights.");
            }}
            className="w-full p-3 text-left bg-orange-50/50 rounded-xl hover:bg-orange-50 transition-colors"
          >
            <p className="font-medium text-secondary">Meal Reminder</p>
            <p className="text-sm text-muted-foreground">Encourage meal analysis</p>
          </button>
          
          <button
            onClick={() => {
              setTitle("PlateMind Insight");
              setMessage("Your glucose tends to be more stable when you eat smaller portions. Keep it up!");
            }}
            className="w-full p-3 text-left bg-orange-50/50 rounded-xl hover:bg-orange-50 transition-colors"
          >
            <p className="font-medium text-secondary">Insight Notification</p>
            <p className="text-sm text-muted-foreground">Share a helpful insight</p>
          </button>
          
          <button
            onClick={() => {
              setTitle("We miss you!");
              setMessage("It's been a while since your last check-in. Come back and track your progress.");
            }}
            className="w-full p-3 text-left bg-orange-50/50 rounded-xl hover:bg-orange-50 transition-colors"
          >
            <p className="font-medium text-secondary">Re-engagement</p>
            <p className="text-sm text-muted-foreground">Bring back inactive users</p>
          </button>
        </div>
      </Card>
    </div>
  );
};

export default AdminNotifications;
